import { defineComponent as _defineComponent } from 'vue'
import { onBeforeMount, ref, watch } from "vue";
import { IAffiliateHistoryItem, ITag } from "@/api/types/user";
import AffiliateChangeHistoryItem from "@/components/affiliate/AffiliateChangeHistoryItem.vue";
import { Affiliate, translateAffiliateField } from "@/services/affiliates/Affiliate";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import { geAffiliateChangeHistory, getAffiliateTags, getAffiliateTagsPage, getUserV3 } from "@/api/user";
import { ITableHeader, showServerError } from "@/utils";
import { CellsType, prepareItems, RawConfig, RouteCellData } from "@/utils/CellsFactory";
import RouteCell from "@/components-UI/table/cells/RouteCell.vue";
import { MAX_LIMIT_TAGS, PAGINATION_PER_PAGE } from "@/configs/global";

interface Props {
    affiliate: Affiliate
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AffiliateChangeHistoryTable',
  props: {
    affiliate: null
  },
  setup(__props: any) {

const props = __props as {
    affiliate: Affiliate
}



const headers: ITableHeader[] = [
    { text: 'Дата/время обновления', value: 'changed_at', sortable: false, width: '150px' },
    { text: 'Объект', value: 'subject', sortable: false },
    { text: 'ID Объекта', value: 'subject_id', sortable: false, align: 'center' },
    { text: 'Какое поле изменено', value: 'translated_field', sortable: false },
    { text: 'Старое значение', value: 'old_value', sortable: false, width: '350px' },
    { text: 'Новое значение', value: 'new_value', sortable: false, width: '350px' },
    { text: 'Кем изменено', value: 'changed_by', sortable: false, align: 'center', width: '200px' },
];

const listOffset = ref(0);
const listLimit = ref(PAGINATION_PER_PAGE);

const changePage = (offset: number) => {
    listOffset.value = offset;
    getHistoryData();
};

const changeLimit = ({ offset, limit }: { offset: number; limit: number }) => {
    listLimit.value = limit;
    listOffset.value = offset;
    getHistoryData();
};

const formattedItems = ref([]);
const total = ref(0);
const isLoading = ref(false);
const managers = ref<{ [key: number]: string }>({});
const tags = ref<{ [key: number]: string }>({});
const isTagsLoaded = ref(false);
let tagsRequest: Promise<void> | null = null;
const methodRouteData: RouteCellData = {
    valueKey: 'subject_id',
    name: 'change-method-request',
    text: 'subject_id',
    isChip: true,
    isTextCenter: true,
};

onBeforeMount(() => {
    getHistoryData();
});

watch(
    () => props.affiliate.id,
    (id, prev) => {
        if (prev == null || id === prev) return;
        listOffset.value = 0;
        listLimit.value = PAGINATION_PER_PAGE;
        getHistoryData();
    },
);

const getHistoryData = async () => {
    try {
        isLoading.value = true;
        const { data, meta } = await geAffiliateChangeHistory(props.affiliate.id, {
            limit: listLimit.value,
            offset: listOffset.value,
        });
        total.value = meta.total;
        await prepareData(data);
        formatItems(data);
    } catch (err) {
        showServerError(err, 'Ошибка загрузки истории изменений');
    } finally {
        isLoading.value = false;
    }
};

const prepareData = async (items: IAffiliateHistoryItem[]): Promise<void> => {
    const managerIds = items
        .filter(item => item.field === 'manager')
        .flatMap(item => [item.old_value, item.new_value] as number[]);

    const requests: Promise<void>[] = [...new Set(managerIds)].map(id => getManagerName(id));

    if (items.some(item => item.field === 'tags')) {
        requests.push(getTags());
    }

    await Promise.all(requests);
};

const cellsConfigs: RawConfig<IAffiliateHistoryItem> = {
    [CellsType.Text]: {
        subject: { template: (item: IAffiliateHistoryItem) => translateAffiliateField(item.subject) },
        translated_field: { template: (item: IAffiliateHistoryItem) => translateAffiliateField(item.field) },
    },
    [CellsType.Date]: {
        changed_at: {},
    },
    [CellsType.Uniq]: {
        subject_id: {},
        old_value: {},
        new_value: {},
    },
};

const formatItems = (items: IAffiliateHistoryItem[]) => {
    const columns = headers.map(h => h.value);
    const rawItems = items.map(i => {
        if (i.field === 'manager') {
            return {
                ...i,
                old_value: managers.value[i.old_value],
                new_value: managers.value[i.new_value],
            };
        }
        if (i.field === 'tags') {
            return {
                ...i,
                old_value: getTagNames(i.old_value),
                new_value: getTagNames(i.new_value),
            };
        }
        return i;
    });
    formattedItems.value = prepareItems(rawItems, cellsConfigs, columns);
    formattedItems.value.forEach(item => {
        item.id = `${item.subject.componentData.value}-${item.subject_id.componentData.value}-${item.changed_at.componentData.value}`;
    });
};

const getManagerName = async (id: number) => {
    if (!id || !!managers.value[id]) return;
    try {
        const { first_name, last_name } = await getUserV3(id, 'employee');
        managers.value[id] = `${first_name || ''} ${last_name || ''}`;
    } catch (err) {
        showServerError(err, 'Ошибка загрузки менеджера');
    }
};

// Эндпоинт отдаёт максимум MAX_LIMIT_TAGS за запрос: по meta.total первого ответа
// добираем остаток страницами, иначе теги сверх лимита пропадут из истории без ошибки.
const fetchAllTags = async (): Promise<ITag[]> => {
    const params = { with_deleted: 1, limit: MAX_LIMIT_TAGS };
    const { data, meta } = await getAffiliateTagsPage({ ...params, offset: 0 });
    const total = meta?.total ?? data.length;

    const restPages: Promise<ITag[]>[] = [];
    for (let offset = MAX_LIMIT_TAGS; offset < total; offset += MAX_LIMIT_TAGS) {
        restPages.push(getAffiliateTags({ ...params, offset }));
    }

    return [data, ...await Promise.all(restPages)].flat();
};

const loadTags = async (): Promise<void> => {
    try {
        const rawTags = await fetchAllTags();
        tags.value = rawTags.reduce((acc: { [key: number]: string }, tag: ITag) => {
            acc[Number(tag.id)] = tag.name;
            return acc;
        }, {});
        isTagsLoaded.value = true;
    } catch (err) {
        showServerError(err, 'Ошибка загрузки тегов');
    } finally {
        tagsRequest = null;
    }
};

// справочник общий для всех строк и страниц истории: грузим один раз и переиспользуем
const getTags = (): Promise<void> => {
    if (isTagsLoaded.value) return Promise.resolve();
    tagsRequest = tagsRequest || loadTags();
    return tagsRequest;
};

const getTagNames = (value: IAffiliateHistoryItem['old_value']): string[] => {
    if (!Array.isArray(value)) return [];
    return (value as number[]).map(id => tags.value[id]).filter(Boolean);
};


return { __sfc: true,props, headers, listOffset, listLimit, changePage, changeLimit, formattedItems, total, isLoading, managers, tags, isTagsLoaded, tagsRequest, methodRouteData, getHistoryData, prepareData, cellsConfigs, formatItems, getManagerName, fetchAllTags, loadTags, getTags, getTagNames, AffiliateChangeHistoryItem, BaseTable, RouteCell }
}

})